import { KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnInit } from '@angular/core';
import { Commands, GoLinks, Results, Search } from '@local/client-contracts';
import { Constants } from '@local/common';
import { isExtension, isNativeWindow } from '@local/common-web';
import { KeyName, normalizeKeys } from '@local/ts-infra';
import { Icon, PopupService } from '@local/ui-infra';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventInfo, EventInfoResource, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { getDefaultProfile } from '@shared/utils/set-icon.util';
import * as moment from 'moment/moment';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { WorkspacesService } from 'src/app/bar/services';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { GoLinksService } from 'src/app/bar/services/go-links.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { ViewItemComponent } from 'src/app/bar/views/results-views/view-item/view-item.component';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { TelemetryTrigger } from '../../../results/models/results-types';
import { PageType } from '../../../results/models/view-filters';
import { GoLinkCommandBuilder, GoLinkCommandBuilderParams, ResultShortcuts } from '../go-link-command-builder.service';
import { GoLinksContextMenuHelper } from '../go-links-context-menu-helper';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';

export interface Visitor {
  accountId: string;
  lastTime: number;
}

@Component({
  selector: 'go-links-view-item',
  templateUrl: './go-links-view-item.component.html',
  styleUrls: ['./go-links-view-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoLinksViewItemComponent extends ViewItemComponent<Search.GoLinkResultItem> implements OnInit {
  readonly VIEWS_TOOLTIP_LIMIT = 10;
  readonly MEMBER_ICON_FALLBACK = getDefaultProfile();
  readonly GO_LINK_ICON_FALLBACK = '/assets/go-links/go-link-default-icon.svg';
  isNative = isNativeWindow();
  iconValue: Icon | any;
  isExtension = isExtension();

  protected showResultSectionsDefault: Results.GoLinksShowResultSections = {
    showTags: true,
    showBullets: true,
    showAvatarList: true,
    enableCommandBar: true,
    showFavoriteIcon: true,
    showContextMenu: true,
  };

  @Input() set model(value: Search.GoLinkResultItem) {
    super.model = value;
    if (this.model?.view) {
      this.iconValue = { ...this.model.view.icon };
    }
  }

  @Input() set visit(value: GoLinks.GetVisitBatchItem) {
    this.initVisitors(value);
  }

  get model() {
    return super.model;
  }

  get name(): PageType {
    return 'golinks';
  }

  private get eventResources(): Partial<EventInfoResource>[] {
    return [
      {
        id: this.model.data.nameId,
        list: this.model.data.group,
        appId: 'go_link',
        type: 'go_link',
        position: this.resultsIndex,
        linkId: this.model.id,
      },
    ];
  }

  avatarsList: AvatarItemModel[] = [];

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    goLinkCommandBuilder: GoLinkCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    protected popupService: PopupService,
    protected keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    protected goLinksService: GoLinksService,
    private goLinkBuilder: GoLinkCommandBuilder,
    protected flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      goLinkCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      workspacesService,
      searchPopUpService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.model.showResultSections) {
      this.checkShowResultSections();
    }
  }

  private checkShowResultSections() {
    this.setShowResultSections(this.model.showResultSections);
    this.setShowResultSections(this.showResultSectionsDefault, false);
  }

  protected async updateThumbnailUrl(): Promise<string> {
    if (!this.model?.view?.thumbnail?.url) {
      return;
    }
    this.thumbnail = this.model.view.thumbnail.url;
    return this.thumbnail;
  }

  private initVisitors(visit: GoLinks.GetVisitBatchItem) {
    if (!visit?.entries) {
      this.avatarsList = [];
      return;
    }
    const visits: GoLinks.VisitItem[] = Array.isArray(visit.entries) ? visit.entries : Object.values(visit.entries);
    if (visits.length === 1 && visits[0].accountId === this.goLinksService.accountId) return;
    this.avatarsList = visits
      .map((item, index) => {
        const account = this.goLinksService.workspaceAccounts?.find((account) => account.id === item.accountId);
        return account
          ? {
              imgUrl: account?.picture,
              name: `${account?.firstName || ''}  ${account?.lastName || ''}`,
              description: moment(item.lastTime).fromNow(),
              email: account?.email,
            }
          : undefined;
      })
      ?.filter((a) => !!a);
    this.cdr.markForCheck();
  }

  protected toggleFavorite(trigger: TelemetryTrigger): Promise<void> {
    if (!this.workspacesService.isFeatureDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG)) {
      super.toggleFavorite(trigger);
      this.model.isFavorite = !this.model.isFavorite;
      this.cdr.markForCheck();

      this.invoke.emit({
        command: {
          type: 'go-links-change-favorite-status',
          status: this.model.isFavorite ? 'add' : 'remove',
          id: this.model.data.id,
        } as Commands.GoLinksChangeFavoriteStatus,
        trigger,
        eventInfo: { label: `${this.model.isFavorite ? 'add' : 'remove'}_favorite`, target: trigger },
        invokerItem: this.model,
      });
      return;
    }
  }

  async setShortcutsHandlers() {
    await super.setShortcutsHandlers();

    if (!this.isNative) return;

    const shortcut = await this.keyboardService.loadShortcuts<ResultShortcuts>(['_goLink']);
    let keys: string[];

    if (shortcut.copyGoLink) {
      keys = normalizeKeys(shortcut.copyGoLink).map((k) => k.toLowerCase());
      if (keys) {
        this.shortcutsHandlers.push(this.createNewItem(keys, 'copyGoLink'));
      }
    }
    if (this.goLinksService.canEditOrDelete(this.model.data)) {
      if (shortcut.edit) {
        keys = normalizeKeys(shortcut.edit).map((k) => k.toLowerCase());
        if (keys) {
          this.shortcutsHandlers.push(this.createNewItem(keys, 'edit'));
        }
      }
      if (shortcut.delete) {
        keys = normalizeKeys(shortcut.delete).map((k) => k.toLowerCase());
        if (keys) {
          this.shortcutsHandlers.push(this.createNewItem(keys, 'deleted'));
        }
      }
    }
  }

  private createNewItem(keys, trait) {
    return {
      fn: (item: GoLinksViewItemComponent, trigger: TelemetryTrigger, eventInfo) => {
        item.copyBullet(trigger, eventInfo, trait);
      },
      keyBinding: keys as KeyName[],
    };
  }

  private copyBullet(trigger: TelemetryTrigger, eventInfo: Partial<EventInfo>, traitName: string): boolean {
    const isTextSelected = document.getSelection() && document.getSelection().toString() !== '';
    const trait = this.model.data;
    if (!isTextSelected && trait) {
      const command: GoLinksCommand = { type: '' };
      if (traitName === 'copyGoLink' && trait) {
        command.type = 'copy-clipboard';
        command.value = this.model.view.title.text;
      }
      if (traitName === 'edit' && trait) {
        command.type = 'go-links-edit';
      }
      if (traitName === 'deleted' && trait) {
        command.type = 'go-links-delete';
      }
      this.onInvoke({ command, trigger, eventInfo });
      return true;
    }
    return false;
  }

  async onInvoke(invokeData: InvokeCommand): Promise<void> {
    invokeData.eventInfo = { resources: this.eventResources };

    if (invokeData.command?.type === 'go-links-edit') {
      this.goLinksService.openPopup(this.model);
      return;
    }

    if (invokeData.command?.type === 'go-links-delete') {
      this.goLinksService.delete(this.model.data.id);
      return;
    }

    if (invokeData.command?.type === 'go-links-share') {
      this.goLinksService.openShareGoLinkPopup(this.model.data, 'edit');
      return;
    }

    this.invoke.emit({ ...invokeData, invokerItem: this.model });
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    const itemsToRemove = [];

    const disabledCollections = await this.workspacesService.isFeatureFlagDisabled(Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG);
    if (disabledCollections) {
      itemsToRemove.push('add_to_collection');
    }
    this.eventsService.event('go_links.open_menu', {
      resources: this.eventResources,
      target: 'mouse_click',
      location: { title: super.getLocation() },
    });
    const ref = await this.contextMenuHelper.open(
      event,
      this.model,
      source,
      null,
      null,
      false,
      true,
      true,
      5,
      false,
      itemsToRemove,
      this.actionsReadOnly
    );
    ref.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.contextOpen.emit();
  }

  protected sendEvent(ev: Partial<EventInfo>, key?: string) {
    ev.resources = this.eventResources;
    this.eventsService.event(key || 'context_menu.open', ev);
  }

  protected async buildPresentableCommandsParams(): Promise<GoLinkCommandBuilderParams> {
    const itemsRequest = await super.buildPresentableCommandsParams();
    let edit: KeyValue<string, string>;
    let deleted: KeyValue<string, string>;
    let share: KeyValue<string, string>;
    let copyGoLinks: KeyValue<string, string>;
    if (this.model.view.title.text) {
      const copy = this.model.view.title.text;
      copyGoLinks = { key: 'copyGoLinks', value: copy };
    }
    if (this.goLinksService.canEditOrDelete(this.model.data)) {
      if (this.model.data.nameId) {
        edit = { key: 'edit', value: this.model.data.nameId };
      }
      if (this.model.data.id) {
        deleted = { key: 'delete', value: this.model.data.id };
        share = { key: 'share', value: this.model.data.id };
      }
    }
    return { ...itemsRequest, copyGoLink: copyGoLinks, edit, delete: deleted, share };
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new GoLinksContextMenuHelper(
      this.goLinkBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService,
      this.goLinksService
    );
  }

  galleryInvoke($event: { event: MouseEvent; type: string }) {
    switch ($event.type) {
      case 'contextmenu':
        $event.event.stopPropagation();
        this.openContextMenu($event.event, 'actions');
        break;
      case 'title':
        this.onInvoke({ command: this.model?.view?.title?.onClick, source: 'title' });
        break;
      case 'checkbox':
        this.changeSelection.emit($event.event);
        break;
    }
  }
}
export interface GoLinksInvokeCommand extends InvokeCommand {
  subType?: subType;
}
export declare class GoLinksCommand {
  type: string;
  value?: string;
}

export type subType = 'edit' | 'delete' | 'copyGoLinks';
